import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react'
import { FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX } from '../../../constants'
import { useSaleManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectIsSaleRecalculationLocked } from '../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'

type PaymentRecalculationLockContextValue = {
  arePaymentActionsLocked: boolean
  lockPaymentActionsUntilRecalculationCompletes: () => void
}

const defaultContextValue: PaymentRecalculationLockContextValue = {
  arePaymentActionsLocked: false,
  lockPaymentActionsUntilRecalculationCompletes: () => {},
}

const PaymentRecalculationLockContext = createContext<PaymentRecalculationLockContextValue>(
  defaultContextValue
)

/**
 * TODO(integrated_payment_overpayment_fix cleanup): Once this FF is fully
 * rolled out and removed, this context and its provider can be deleted entirely.
 * At that point switch all locking to the Redux counter:
 *
 *   1. Delete this file and PaymentRecalculationLockContext.test.tsx
 *   2. Remove <PaymentRecalculationLockProvider> from PaymentPanel.tsx
 *   3. Remove lockPaymentActionsUntilRecalculationCompletes() call sites:
 *        - handleCustomerSelect in PaymentPanel.tsx
 *        - RemoveCustomerButton.tsx
 *   4. Replace every usePaymentRecalculationLock() call with
 *      useAppSelector(selectIsSaleRecalculationLocked) directly
 *   5. Drop the explicit overpaymentFixEnabled guard in PayPalRefundButton
 *      and PaymentRequestButton — the Redux selector becomes the single
 *      source of truth and no extra FF check is needed
 */
export const PaymentRecalculationLockProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const saleManager = useSaleManager()

  const [contextLocked, setContextLocked] = useState(false)

  const lockPaymentActionsUntilRecalculationCompletes = useCallback(() => {
    setContextLocked(true)
  }, [])

  useEffect(() => {
    const onCalculatingEnd = () => setContextLocked(false)
    saleManager.on('saleManager:saleCalculating:end', onCalculatingEnd)
    return () => {
      saleManager.off('saleManager:saleCalculating:end', onCalculatingEnd)
    }
  }, [saleManager])

  // New Redux-based lock: covers the 0–250ms debounce window and the slow
  // updateCustomerBalances() API call — both gaps missed by the event-based
  // lock above. FF-gated: returns false when integrated_payment_overpayment_fix
  // is OFF, so existing behaviour is fully preserved for FF-off users.
  const reduxLocked = useAppSelector(selectIsSaleRecalculationLocked)
  const overpaymentFixEnabled = getFeatureFromCache(
    FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX
  )

  // FF ON: Redux is the only UI authority so a failed addCustomer/removeCustomer
  // cannot leave contextLocked stuck true (saleCalculating:end never fires).
  // FF OFF: legacy event lock only — reduxLocked is already always false.
  const value = useMemo(
    () => ({
      arePaymentActionsLocked: overpaymentFixEnabled
        ? reduxLocked
        : contextLocked,
      lockPaymentActionsUntilRecalculationCompletes,
    }),
    [
      overpaymentFixEnabled,
      contextLocked,
      reduxLocked,
      lockPaymentActionsUntilRecalculationCompletes,
    ]
  )

  return (
    <PaymentRecalculationLockContext.Provider value={value}>
      {children}
    </PaymentRecalculationLockContext.Provider>
  )
}

export const usePaymentRecalculationLock = () => {
  return useContext(PaymentRecalculationLockContext)
}
